// Mirrors wine2trace-server/services/operations/helpers/evaporationEstimates.js
// (same duplication convention as CAPACITY_CATALOG and other enum-ish catalogs
// shared between FE/BE) -- kept in sync manually.
//
// Reference values are for a climate-controlled Italian cantina (~3-6% total
// annual loss, peaking in summer); NOT based on whisky/spirits warehouse
// figures, which run far higher and assume uncontrolled storage and much
// higher proof. Indexed Jan(0)..Dec(11), reference calendar for the northern
// hemisphere.
const MONTHLY_EVAPORATION_PCT_NORTH = [0.5, 0.5, 0.7, 0.8, 1.0, 1.5, 2.0, 2.0, 1.5, 1.0, 0.7, 0.5];

// Countries whose wine-growing regions are in the southern hemisphere: same
// seasonal shape, shifted 6 months (their summer is Dec-Feb, not Jun-Aug).
const SOUTHERN_HEMISPHERE_COUNTRIES = ["AR", "CL", "UY", "ZA", "AU", "NZ"];

const getMonthlyEvaporationPct = (countryCode, month) => {
  const isSouthernHemisphere = SOUTHERN_HEMISPHERE_COUNTRIES.includes(countryCode);
  const referenceMonth = isSouthernHemisphere ? (month + 6) % 12 : month;
  return MONTHLY_EVAPORATION_PCT_NORTH[referenceMonth];
};

/**
 * Fill status for a catasta tank -- same two facts computed server-side
 * (full_capacity, evaporation_anchor_date, see services/tanksService.js) but
 * the day-by-day % math is done here to avoid an extra fetch per tank.
 *
 * @param {Object} tank - A tank record with quantity, full_capacity, evaporation_anchor_date.
 * @param {string} countryCode - Winery's ISO 3166-1 alpha-2 country code.
 * @returns {{structuralGap: number, estimatedEvaporationPct: number, estimatedEvaporationLiters: number}|null}
 *   null if the tank isn't a catasta with enough data to estimate (no anchor date yet, e.g. never filled).
 */
export const getCatastaFillEstimate = (tank, countryCode) => {
  const fullCapacity = Number(tank?.full_capacity) || 0;
  if (!fullCapacity) return null;

  const currentQuantity = Number(tank?.quantity) || 0;
  const structuralGap = Math.max(0, fullCapacity - currentQuantity);

  if (!tank?.evaporation_anchor_date) {
    return { structuralGap, estimatedEvaporationPct: 0, estimatedEvaporationLiters: 0 };
  }

  const cursor = new Date(tank.evaporation_anchor_date);
  const now = new Date();
  let estimatedEvaporationPct = 0;
  while (cursor < now) {
    const daysInMonth = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0).getDate();
    estimatedEvaporationPct += getMonthlyEvaporationPct(countryCode, cursor.getMonth()) / daysInMonth;
    cursor.setDate(cursor.getDate() + 1);
  }

  return {
    structuralGap,
    estimatedEvaporationPct,
    estimatedEvaporationLiters: (currentQuantity * estimatedEvaporationPct) / 100,
  };
};
